import * as joda from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { APISpec } from "trader.ts/router";
import { Source } from "trader.ts/types/source";
import { dyn } from "trader.ts/ui";
import {
  Field,
  Form,
  checkBox,
  constantField,
  selectBox,
  timeBox,
} from "trader.ts/ui.forms";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  enrollmentrequestid,
  enrollmentrequestprivateid,
  formulachargemode,
  instrumentid,
  internalteacherid,
  periodic_payment_method,
  periodid,
  price_formula,
  studentid,
  teacherid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as planningstyle from "../../../styles/planning.css.classes";
import * as style from "../../../styles/style.css.classes";
import { constantFieldWithRender } from "../../client/constantfieldwithrender";
import { renderSaveButton } from "../../client/savebutton";
import {
  country,
  formulaFull,
  non_periodic_payment_scheme,
  periodic_payment_scheme,
} from "../../domain/config";
import {
  adaptInvoiceLineToLg,
  adaptInvoiceLinesToStudent,
  periodfeeAfterChoice,
} from "../../domain/finance";
import {
  invoicelineRequestedOptional,
  invoicelineStudent,
} from "../../domain/finance/types";
import { holiday, period } from "../../domain/periods";
import { formulaColorToPlanningColors, makeGradient } from "../../lib/colors";
import { isEqual } from "../../lib/isequal";
import { isNil } from "../../lib/istype";
import {
  dateInDaterange,
  getFirstWeekdayFrom,
  getMonday,
  makeDateRange,
} from "../../lib/time";
import {
  formulaType,
  invoicelinetype,
  parsePhoneType,
  showPhoneType,
  singleDueDateCalculation,
} from "../../parsing_from_ur";
import {
  ddMM,
  ddMMMMyyyy,
  renderAnswer,
  renderCurrency,
  renderFirstNameLastName,
  renderTimePlusWeekday,
  renderTimeRange,
} from "../../render";
import * as routes from "../../routes";
import { translateWeekday } from "../../translations_specific";
import { schoolurl } from "../../types";
import { invoiceAndExpectedPaymentsField } from "../planning/enrollmentform";
import {
  errorField,
  locationField,
  makeFormulaValuesField,
  pickDatesOnYearCalendarField,
  renderWeeklyLessonsPlanningInformation,
} from "../planning/lessongroupform";
import { locationWithSuggestion } from "../planning/server";
import type {
  enrollmentrequest_full,
  groupEnrReq,
  privateEnrReq,
} from "./server";
import currency from "currency.js";
import { match } from "ts-pattern";

export function render(opts: {
  country: country;
  schoolurl: schoolurl;
  allFormulas: formulaFull[];
  er: enrollmentrequest_full;
  period: period & { holidays: holiday[] };
  eachPeriodFeePaymentSettings: {
    each_period_fee_due_date: singleDueDateCalculation;
    each_period_fee_pay_during_online: boolean;
  };
}): HTMLElement | HTMLElement[] {
  const er = opts.er;
  const today = joda.LocalDate.now();
  function wrapSimple(s: string) {
    return (
      <div className={style.panel + style.card + style.card_large}>
        <b>{s}</b>
      </div>
    );
  }

  const statusSpecific = (function () {
    switch (er.status) {
      case "InvitationER":
        return wrapSimple(
          "Dit is een uitnodiging voor een herinschrijving",
        );
      case "InvitationRejectedER":
        return wrapSimple(
          "Dit is een uitnodiging voor een herinschrijving en werd geweigerd",
        );
      case "Reserved":
        return wrapSimple(
          "Deze reservatie wacht tot %date op herinschrijving door de student".replace(
            "%date",
            er.reserveduntil ? ddMMMMyyyy(er.reserveduntil) : "",
          ),
        );
      case "WaitingForEmailConfirmationER":
        return wrapSimple(
          "Deze inschrijvingsaanvraag wacht op de student om zijn/haar emailadres te bevestigen via de link in de uitgestuurde email",
        );
      case "WaitingForEmailConfirmationTimedOutER":
        return wrapSimple(
          "Deze inschrijvingsaanvraag wachtte te lang op de student om zijn/haar emailadres te bevestigen via de link in de uitgestuurde email",
        );
      case "WaitingForPaymentER":
      case "WaitingForPaymentAtProviderER":
        return wrapSimple(
          "Deze inschrijvingsaanvraag wacht op online betaling",
        );
      case "WaitingForPaymentTimedOutER":
      case "WaitingForPaymentAtProviderTimedOutER":
        return wrapSimple(
          "Deze inschrijvingsaanvraag wachtte te lang op online betaling",
        );
      case "ConvertedToEnrollment":
        return wrapSimple(
          "Deze inschrijvingsaanvraag werd goedgekeurd",
        );
      case "DisprovedER":
        return wrapSimple("Deze inschrijvingsaanvraag werd afgekeurd");
      case "WaitingForApprovalER":
        if (er.contents.tag === "group") {
          return mkApproveGroupLessonEnrollmentrequestForm(
            opts.country,
            opts.schoolurl,
            er.student,
            er.id,
            opts.period,
            opts.allFormulas,
            er.contents,
            er.invoice?.payments || [],
            er.requestedOptionalInvoicelines,
            opts.eachPeriodFeePaymentSettings,
          );
        } else {
          const form = mkApprovePrivateLessonEnrollmentrequestForm(
            opts.country,
            opts.schoolurl,
            er.student,
            opts.allFormulas,
            opts.period,
            er.contents,
            er.invoice?.payments || [],
            er.requestedOptionalInvoicelines,
            opts.eachPeriodFeePaymentSettings,
          );
          return renderApprovePrivateLessonEnrollmentrequestForm(
            er.id,
            er.contents,
            er.student,
            form,
          );
        }
      default:
        return checkAllCasesHandled(er.status);
    }
  })();

  const disprovingS = new Source(false);

  function renderDisprovalPanel(): HTMLElement {
    const texts =
      er.status === "WaitingForApprovalER"
        ? {
            question: "Keur inschrijving af",
            confirmation: "Ben je zeker dat je deze inschrijving wilt afkeuren?",
          }
        : er.status === "Reserved"
        ? {
            question: "Verwijder reservatie",
            confirmation: "Ben je zeker dat je deze reservatie wilt verwijderen?",
          }
        : er.status === "InvitationER"
        ? {
            question: "Verwijder uitnodiging",
            confirmation: "Ben je zeker dat je deze uitnodiging wilt verwijderen?",
          }
        : er.status === "WaitingForPaymentTimedOutER" ||
          er.status === "WaitingForPaymentAtProviderTimedOutER" ||
          er.status === "WaitingForEmailConfirmationTimedOutER"
        ? {
            question: "Verwijder",
            confirmation: "Ben je zeker dat je dit wilt verwijderen?",
          }
        : null;

    if (texts === null) {
      return <span></span>;
    } else {
      return (
        <section className={style.card + style.card_large}>
          <article>
            <article>
              <button onclick={() => disprovingS.set(true)} style="width: 100%">
                {texts.question}
              </button>
            </article>
            {dyn(disprovingS, (disproving) => {
              if (disproving) {
                return (
                  <div className={style.panel + style.slide_in}>
                    <article>{texts.confirmation}</article>
                    <div style="text-align: right">
                      <button
                        className={style.button_em_down}
                        onclick={() => disprovingS.set(false)}
                      >
                        {"Annuleer"}
                      </button>
                      {renderSaveButton({
                        save: async function () {
                          const res = await rpc(
                            routes.disproveEnrollmentRequestSpec,
                            undefined,
                            {
                              enrollmentrequestid: er.id,
                            },
                          );
                          return {
                            tag: "success",
                            redirectUrl: res.redirectTo,
                          };
                        },
                        text: {
                          normal: texts.question,
                          success: "Gelukt!",
                        },
                        reload: true,
                        timeout: 1000,
                        style:
                          "background-color: var(--grey-700); color: var(--grey-100); border-color: var(--grey-900);",
                      })}
                    </div>
                  </div>
                );
              } else {
                return <span></span>;
              }
            })}
          </article>
        </section>
      );
    }
  }

  return (
    <div className={style.equal_columns}>
      <div>
        <section className={style.card + style.card_large}>
          <h1>
            {"Student"}:{" "}
            <a
              href={routes.studentOverviewRoute.link({
                studentid: er.student.id,
              })}
            >
              {renderFirstNameLastName(er.student)}
            </a>
            <br />
          </h1>

          {er.student.phones.map((p) => {
            return (
              <article className={style.text_with_icon}>
                <i className={FA.icon_phone_solid}></i>
                <div>
                  {p.description === null
                    ? ""
                    : showPhoneType(parsePhoneType(p.description)) + ": "}
                  {p.number.toString()}
                </div>
              </article>
            );
          })}

          {er.student.emails.map((em) => {
            return (
              <article className={style.text_with_icon}>
                <i className={FA.icon_envelope_solid}></i>
                <div>{em.email}</div>
              </article>
            );
          })}

          {er.student.birthday === null ? (
            <span></span>
          ) : (
            <article className={style.text_with_icon}>
              <i className={FA.icon_birthday_cake}></i>
              <div>{ddMMMMyyyy(er.student.birthday)}</div>
            </article>
          )}

          <article className={style.text_with_icon}>
            <i className={FA.icon_envelope_solid}></i>
            <div>
              <a
                href={routes.messagesRoute.link({
                  selected: { student: er.student.id },
                  showall: false,
                })}
              >
                {"Stuur een bericht"}
              </a>
            </div>
          </article>
        </section>

        {er.student.comments === null || er.student.comments.trim() === "" ? (
          <span></span>
        ) : (
          <section className={style.card + style.card_large}>
            <h1>{"Extra info"}</h1>
            <article>{er.student.comments}</article>
          </section>
        )}

        <section className={style.card + style.card_large}>
          <h1>{"Gevraagde inschrijving"}</h1>

          <article className={style.text_with_icon}>
            <i className={FA.icon_clipboard_solid}></i>
            <div>{er.contents.formula.description}</div>
          </article>

          <article className={style.text_with_icon}>
            <i className={FA.icon_glasses}></i>
            <div>
              {er.contents.teacher !== null
                ? renderFirstNameLastName(er.contents.teacher)
                : "Geen docent ingevuld"}
            </div>
          </article>

          <article className={style.text_with_icon}>
            <i className={FA.icon_music_solid}></i>
            <div>{er.contents.instrument.description}</div>
          </article>

          <article className={style.text_with_icon}>
            <i className={FA.icon_clock_regular}></i>
            <div>
              {renderTimePlusWeekday(er.contents.time, er.contents.weekday)}
            </div>
          </article>

          <article className={style.text_with_icon}>
            <i className={FA.icon_calendar_alt_regular}></i>
            <div>
              {er.contents.tag === "group" && er.contents.trial_lesson
                ? "Proefles"
                : "Eerste les op %d".replace(
                    "%d",
                    er.contents.firstlesson === null
                      ? ""
                      : ddMMMMyyyy(er.contents.firstlesson),
                  )}
            </div>
          </article>

          {er.status === "WaitingForApprovalER" ? (
            <article className={style.text_with_icon}>
              <i className={FA.icon_save_solid}></i>
              <div>
                {"Aangevraagd op %d".replace(
                  "%d",
                  ddMMMMyyyy(er.updated.toLocalDate()),
                )}
              </div>
            </article>
          ) : (
            <span></span>
          )}

          {er.contents.tag === "group" ? (
            <article className={style.text_with_icon}>
              <i className={FA.icon_link_solid}></i>
              <div>
                <a
                  href={routes.lessongroup_generalRoute.link({
                    lessongroupid: er.contents.id,
                  })}
                >
                  {"Bekijk lessenreeks"}
                </a>
              </div>
            </article>
          ) : (
            <span></span>
          )}

          {!isNil(er.contents.teacher) ? (
            <article className={style.text_with_icon}>
              <i className={FA.icon_calendar_alt_regular}></i>
              <div>
                <a
                  href={routes.planningRoute.link({
                    time: er.contents.time,
                    teacher: er.contents.teacher.internalteacherid,
                    date:
                      er.contents.firstlesson ||
                      getFirstWeekdayFrom(today, er.contents.weekday),
                  })}
                >
                  {"Bekijk op planning"}
                </a>
              </div>
            </article>
          ) : (
            <span></span>
          )}
        </section>

        {er.questions.length > 0 ? (
          <section className={style.card + style.card_large}>
            <h1>{"Extra vragen"}</h1>
            {er.questions
              .orderBy((q) => q.ordering)
              .map((question) => {
                const content = renderAnswer(question);
                return (
                  <article>
                    <div>{question.label}</div>
                    <div>
                      <b>{content}</b>
                    </div>
                  </article>
                );
              })}
          </section>
        ) : (
          []
        )}

        {er.invoice !== null && er.invoice.amount_paid.intValue > 0 ? (
          <section className={style.card + style.card_large}>
            <h1>{"Reeds betaald"}</h1>
            <article className={style.text_with_icon}>
              <i className={FA.icon_euro_sign_solid_1}></i>
              <div>{renderCurrency(er.invoice.amount_paid)}</div>
            </article>
          </section>
        ) : (
          <span></span>
        )}

        {renderDisprovalPanel()}
      </div>

      <div>{statusSpecific}</div>
    </div>
  );
}

export function mkApprovePrivateLessonEnrollmentrequestForm(
  country: country,
  schoolurl: schoolurl,
  student: {
    id: studentid;
    firstname: string;
    lastname: string;
    birthday: joda.LocalDate | null;
  },
  allFormulas: formulaFull[],
  period: {
    id: periodid;
    firstday: joda.LocalDate;
    lastday: joda.LocalDate;
    holidays: holiday[];
  },
  privenrreq: privateEnrReq,
  payments: {
    amount: currency;
    paymentproviderid: string | null;
  }[],
  requestedOptionalInvoicelines: invoicelineRequestedOptional[],
  eachPeriodFeePaymentSettings: {
    each_period_fee_due_date: singleDueDateCalculation;
    each_period_fee_pay_during_online: boolean;
  },
  // For testing
  rpcImpl?: <Parameters, Body, Returns>(
    spec: APISpec<Parameters, Body, Returns>,
    params: Parameters,
    b: Body,
  ) => Promise<Returns>,
): Field<{
  date: joda.LocalDate;
  time: joda.LocalTime;
  formula: formulaFull;
  formulaValues: {
    numberoflessons: number;
    maxnumberofstudents: number;
    lessonduration: number;
    invoicelines: {
      description: string;
      amount_with_vat: price_formula;
      amount_without_vat: price_formula | null;
      vat: number;
      type_: invoicelinetype;
      chargemode: formulachargemode;
    }[];
    type_: formulaType;
  };
  teacher: {
    id: teacherid;
    internalteacherid: internalteacherid;
    firstname: string;
    lastname: string;
    instruments: instrumentid[];
  };
  lessondates: {
    lessons: joda.LocalDate[];
    holidaysSkipped: {
      date: joda.LocalDate;
      description: string;
    }[];
  };
  location: locationWithSuggestion;
  invoice_and_expected_payments: {
    invoice: invoicelineStudent[];
    payment_scheme: non_periodic_payment_scheme | periodic_payment_scheme;
    expected_payments: {
      amount: currency;
      date: joda.LocalDate;
      pay_during_online_enrollment: boolean;
      periodic_payment_method: periodic_payment_method | null;
    }[];
    periodfee: periodfeeAfterChoice;
  };
}> {
  const today = joda.LocalDate.now();
  const form = new Form({})
    .addField("date", [], () => {
      // Today > selected -> take today
      // periodstart > selected -> take period
      // today > periodstart -> take period
      const selecteddate = privenrreq.firstlesson;
      const firstWeekOptions = makeDateRange(
        getMonday(selecteddate),
        getFirstWeekdayFrom(selecteddate, joda.DayOfWeek.SUNDAY),
      ).map((d) =>
        pushDateForward({
          prohibited: period.holidays,
          minDate: [period.firstday, today].maxBy((d) => d.toEpochDay())!,
          d,
        }),
      );
      const nextWeekOptions = privenrreq.formula.startsfromperiod
        ? []
        : makeDateRange(
            firstWeekOptions.first()!.plusDays(7),
            firstWeekOptions.last()!.plusDays(7),
          ).map((d) =>
            pushDateForward({
              prohibited: [
                ...period.holidays,
                ...firstWeekOptions.map((d) => ({ firstday: d, lastday: d })),
              ],
              minDate: [period.firstday, today].maxBy((d) => d.toEpochDay())!,
              d,
            }),
          );

      const options = firstWeekOptions
        .concat(nextWeekOptions)
        .orderBy((d) => d.toEpochDay());

      return selectBox({
        values: options,
        initial: options.find((d) =>
          isEqual(d.dayOfWeek(), privenrreq.firstlesson.dayOfWeek()),
        ),
        show: (d: joda.LocalDate) =>
          translateWeekday(d.dayOfWeek()) + " (" + d.format(ddMM) + ")",

        label: "Startdatum",
        groups:
          nextWeekOptions.length > 0
            ? {
                groupnames: ["Eerste week", "Volgende week"],
                assignToGroup: (d) =>
                  firstWeekOptions.some((fwd) => fwd.isEqual(d))
                    ? "Eerste week"
                    : "Volgende week",
              }
            : undefined,
      });
    })
    .addField("time", [], () =>
      timeBox({ initialVal: privenrreq.time, label: "Tijdstip" }),
    )
    .addField("formula", [], () => {
      const options = allFormulas
        .filter((f) => f.type_.tag === "Private")
        .orderBy((f) => f.description);
      return selectBox({
        values: options,
        initial:
          options.find((f) => f.id === privenrreq.formula.id) || options[0],
        show: (f) => f.description,
        label: "Formule",
      });
    })
    .addField("formulaValues", ["formula"], async ({ formula }) => {
      const deviateFromFormulaS = new Source(false);
      return makeFormulaValuesField<price_formula>(
        // Not important
        { sources: { durationS: new Source(0) } },
        deviateFromFormulaS,
        {
          type_: formula.type_,
          numberoflessons: formula.numberoflessons,
          lessonduration: formula.lessonduration,
          invoicelines: formula.invoicelines,
        },
        formula,
        country,
      );
    })
    .addField("showAllTeachers", [], async () => {
      const field = await checkBox({
        initialVal: false,
        label: "Toon ook docenten die dit instrument niet geven maar wel tijd hebben",
        class: style.checkbox,
        style: "font-size: 0.85rem;",
      });
      return {
        ...field,
        render: () => (
          <article style="margin-top: -8px;">{field.render()}</article>
        ),
      };
    })
    .addField(
      "teacher_",
      ["showAllTeachers", "date", "time", "formula", "formulaValues"],
      async ({ showAllTeachers, date, time, formula, formulaValues }) => {
        const teachers = await (rpcImpl || rpc)(
          routes.getPossibleTeachersForEnrollmentRequestSpec,
          undefined,
          {
            dateFrom: date,
            numberOfLessons: formulaValues.numberoflessons,
            startFromPeriod: formula.startsfromperiod,
            lessonduration: formulaValues.lessonduration,
            time: time,
            planningMode: formula.planningmode,
          },
        );
        const teachersShowing = showAllTeachers
          ? teachers
          : teachers.filter((t) =>
              t.teacher.instruments.includes(privenrreq.instrument.id),
            );
        const preSelectedInternalTeacherId =
          privenrreq.teacher?.internalteacherid;
        return selectBox({
          values: teachersShowing,
          initial:
            preSelectedInternalTeacherId !== null
              ? teachersShowing.find(
                  (t) =>
                    t.teacher.internalteacherid ===
                    preSelectedInternalTeacherId,
                ) || teachersShowing[0]
              : teachersShowing[0],
          show: (t) =>
            renderFirstNameLastName(t.teacher) +
            (t.teacher.has_corresponding_teaching_period
              ? ""
              : ` (geen lesperiode) `) +
            (t.dates.tag === "error" ? ` (${t.dates.error})` : ""),
          label: "Docent",
          isDisabled: (t) => t.dates.tag === "error",
        });
      },
    )
    .addField("teacher", ["teacher_"], ({ teacher_ }) => {
      if (teacher_.dates.tag !== "error") {
        return constantField(teacher_.teacher);
      } else {
        return errorField<{
          id: teacherid;
          internalteacherid: internalteacherid;
          firstname: string;
          lastname: string;
          instruments: instrumentid[];
        }>(teacher_.dates.error);
      }
    })
    .addField(
      "lessondates",
      ["teacher_", "formula", "formulaValues"],
      function ({ teacher_, formula, formulaValues }) {
        if (teacher_.dates.tag === "error") {
          return errorField<{
            lessons: joda.LocalDate[];
            holidaysSkipped: {
              date: joda.LocalDate;
              description: string;
            }[];
          }>("");
        } else {
          const dates = teacher_.dates;
          if (formula.planningmode === "Weekly") {
            const renderedInfo = renderWeeklyLessonsPlanningInformation(
              teacher_.dates,
              formula,
            );
            return constantFieldWithRender(teacher_.dates, renderedInfo);
          } else if (
            formula.planningmode === "Flex" ||
            formula.planningmode === "Biweekly" ||
            formula.planningmode === "Manual"
          ) {
            const pm = formula.planningmode;
            return Promise.resolve(
              new Form({})
                .addField("lessons", [], () =>
                  pickDatesOnYearCalendarField({
                    initial: dates.lessons,
                    skip: dates.complementLessons || [],
                    max: formulaValues.numberoflessons,
                    period: period,
                    nextPeriod: null, // I'm lazy
                    limitWeekday: match(pm)
                      .with("Biweekly", () => privenrreq.weekday)
                      .with("Manual", "Flex", () => null)
                      .exhaustive(),
                    showFlexExplanation: match(pm)
                      .with("Biweekly", "Manual", () => false)
                      .with("Flex", () => true)
                      .exhaustive(),
                  }),
                )
                .addField("holidaysSkipped", [], () =>
                  constantField(dates.holidaysSkipped),
                )
                .build(),
            );
          } else {
            return checkAllCasesHandled(formula.planningmode);
          }
        }
      },
    )
    .addField(
      "location",
      ["time", "lessondates", "teacher", "formulaValues"],
      ({ time, lessondates, teacher, formulaValues }) => {
        if (lessondates.lessons.length === 0) {
          return errorField<locationWithSuggestion>(
            "Minstens 1 les nodig",
          );
        } else {
          return locationField(
            lessondates.lessons[0],
            time,
            formulaValues,
            { teacher: teacher },
            { id: privenrreq.instrument.id },
            null,
            null,
            rpcImpl,
          );
        }
      },
    )
    .addField(
      "invoice_and_expected_payments",
      ["formulaValues", "teacher_", "formula", "date"],
      async ({ formulaValues, teacher_, formula, date }) => {
        const dates = teacher_.dates;
        if (dates.tag === "error") {
          throw new Error("No planning");
        }
        const formulaIls = formulaValues.invoicelines.map((il) =>
          adaptInvoiceLineToLg(il),
        );
        const ils: (invoicelineStudent & { type_: invoicelinetype })[] =
          adaptInvoiceLinesToStudent(
            country,
            schoolurl,
            student.birthday,
            {
              tag: "applicable",
              lessons: {
                tag: "lessongroup",
                lessons: dates.lessons,
                startedlessonson: date,
              },
            },
            formulaIls,
          );
        return invoiceAndExpectedPaymentsField(
          ils,
          requestedOptionalInvoicelines,
          formula.payment_schemes,
          { tag: "existing", id: student.id, birthday: student.birthday },
          period.id,
          {
            ...eachPeriodFeePaymentSettings,
            payments,
            dates: {
              firstlesson: dates.lessons.first()!,
              lastlesson: dates.lessons.last()!,
              period_firstday: period.firstday,
              created: joda.LocalDate.now(),
            },
            max_one_optional_line: formula.max_one_optional_line,
            periodfee_required: formula.periodfee_required,
          },
          rpcImpl,
        );
      },
    )
    .build({
      render: (fields) => {
        return (
          <div>
            <div className={style.equal_columns}>
              {fields.date()}
              {fields.time()}
            </div>
            {fields.formula()}
            {fields.formulaValues()}
            <article>
              {fields.teacher_()}
              {fields.showAllTeachers()}
              {fields.teacher()}
              {fields.lessondates()}
            </article>
            {fields.location()}
            {fields.invoice_and_expected_payments()}
          </div>
        );
      },
    });
  return form;
}

function mkApproveGroupLessonEnrollmentrequestForm(
  country: country,
  schoolurl: schoolurl,
  student: {
    id: studentid;
    firstname: string;
    lastname: string;
    birthday: joda.LocalDate | null;
  },
  enrollmentrequestid: enrollmentrequestid,
  period: { firstday: joda.LocalDate; id: periodid },
  allFormulas: formulaFull[],
  lessongroup: groupEnrReq,
  payments: {
    amount: currency;
    paymentproviderid: string | null;
  }[],
  requestedOptionalInvoicelines: invoicelineRequestedOptional[],
  eachPeriodFeePaymentSettings: {
    each_period_fee_due_date: singleDueDateCalculation;
    each_period_fee_pay_during_online: boolean;
  },
) {
  const today = joda.LocalDate.now();
  const formula = allFormulas.find((f) => f.id === lessongroup.formula.id)!;
  const colors = formulaColorToPlanningColors(formula.color);

  const header = (
    <div
      className={style.panel_header}
      style={`background: ${makeGradient(colors)}; color: ${colors.text}`}
    >
      <article className={style.text_with_icon}>
        <i className={FA.icon_glasses}></i>
        <div>
          <b>{renderFirstNameLastName(lessongroup.teacher)}</b>
        </div>
      </article>

      <article style="display: flex">
        <div style="flex: 1">{formula.description}</div>
        <div>
          {translateWeekday(lessongroup.weekday).slice(0, 2) +
            " " +
            renderTimeRange(lessongroup.time, lessongroup.lessonduration)}
        </div>
      </article>
    </div>
  );

  const form = new Form({})
    .addField("invoice_and_expected_payments", [], async () => {
      const lessons = await rpc(
        routes.getActiveLessonsForLessongroupSpec,
        undefined,
        { lessongroupid: lessongroup.id },
      ).then((ls) => ls.map((l) => l.date));
      const ils = adaptInvoiceLinesToStudent(
        country,
        schoolurl,
        student.birthday,
        {
          tag: "applicable",
          lessons: {
            tag: "lessongroup",
            lessons,
            startedlessonson: today,
          },
        },
        lessongroup.invoicelines,
      );
      return invoiceAndExpectedPaymentsField(
        ils,
        requestedOptionalInvoicelines,
        formula.payment_schemes,
        { tag: "existing", id: student.id, birthday: student.birthday },
        period.id,
        {
          ...eachPeriodFeePaymentSettings,
          payments,
          dates: {
            firstlesson: lessongroup.firstlesson!,
            lastlesson: lessongroup.lastlesson!,
            period_firstday: period.firstday,
            created: joda.LocalDate.now(),
          },
          max_one_optional_line: formula.max_one_optional_line,
          periodfee_required: formula.periodfee_required,
        },
      );
    })
    .build();

  return (
    <div className={style.panel + style.card + style.card_large}>
      {header}
      {lessongroup.trial_lesson === true ? (
        <article
          className={style.panel}
          style={`margin-left: -8px;
                  margin-right: -8px;
                  margin-top: -8px;
                  background-color: var(--accent-100);
                  color: var(--accent-900);`}
        >
          <b>{"Proefles"}</b>
        </article>
      ) : (
        <span></span>
      )}
      {form.render()}
      <div style="text-align: right">
        {renderSaveButton({
          formS: form.s,
          text: {
            normal: "Keur goed",
            success: "Gelukt! Klik om de inschrijving te bekijken",
          },
          save: async function (f) {
            await rpc(
              routes.approveGroupLessonEnrollmentRequestSpec,
              undefined,
              {
                lessongroupid: lessongroup.id,
                enrollmentrequestid,
                enr: {
                  student: { tag: "existing", student },
                  trial_lesson: lessongroup.trial_lesson,
                  startslessonson:
                    lessongroup.firstlessoninfuture || joda.LocalDate.now(),
                  invoice: f.invoice_and_expected_payments.invoice,
                  payment_scheme:
                    f.invoice_and_expected_payments.payment_scheme,
                  expected_payments:
                    f.invoice_and_expected_payments.expected_payments,
                  periodfee: f.invoice_and_expected_payments.periodfee,
                },
              },
            );

            return {
              tag: "success",
              link: routes.lessongroup_generalRoute.link({
                lessongroupid: lessongroup.id,
              }),
            };
          },
          reload: true,
        })}
      </div>
    </div>
  );
}

export function renderApprovePrivateLessonEnrollmentrequestForm(
  id: enrollmentrequestid,
  privenrreq: {
    instrument: { id: instrumentid };
    startsinperiod: periodid;
    privid: enrollmentrequestprivateid;
  },
  student: { id: studentid },
  form: ReturnType<typeof mkApprovePrivateLessonEnrollmentrequestForm>,
) {
  return (
    <div className={style.panel + style.card + style.card_large}>
      <div className={style.panel_header + planningstyle.slot_new}>
        <b>{"Goedkeuring nieuwe privéles"}</b>
      </div>

      {form.render()}

      <div style="text-align: right">
        {renderSaveButton({
          formS: form.s,
          save: async function (f) {
            const res = await rpc(
              routes.approvePrivateLessonEnrollmentRequestSpec,
              undefined,
              {
                lessongroup: {
                  teacher: f.teacher,
                  time: f.time,
                  instrument: privenrreq.instrument,
                  formula: f.formula,
                  formulaValues: {
                    ...f.formulaValues,
                    invoicelines: f.formulaValues.invoicelines.map((il) =>
                      adaptInvoiceLineToLg(il),
                    ),
                  },
                  lessondates: f.lessondates,
                  location: f.location,
                  periodid: privenrreq.startsinperiod,
                },
                enrollmentrequestid: id,
                enrollmentrequestprivateid: privenrreq.privid,
                enr: {
                  student: { tag: "existing", student },
                  trial_lesson: false,
                  startslessonson:
                    f.lessondates.lessons.first() || joda.LocalDate.now(),
                  invoice: f.invoice_and_expected_payments.invoice,
                  payment_scheme:
                    f.invoice_and_expected_payments.payment_scheme,
                  expected_payments:
                    f.invoice_and_expected_payments.expected_payments,
                  periodfee: f.invoice_and_expected_payments.periodfee,
                },
              },
            );
            if (res.tag === "success") {
              return {
                tag: "success",
                link: routes.lessongroup_generalRoute.link({
                  lessongroupid: res.lessongroupid,
                }),
              };
            } else {
              return res;
            }
          },
          text: {
            normal: "Keur inschrijving goed",
            success: "Gelukt! Klik om de inschrijving te bekijken",
          },
          reload: true,
          timeout: 1000 * 60 * 60,
        })}
      </div>
    </div>
  );
}

function pushDateForward(opts: {
  prohibited: { firstday: joda.LocalDate; lastday: joda.LocalDate }[];
  minDate: joda.LocalDate;
  d: joda.LocalDate;
}): joda.LocalDate {
  let curr = opts.d;
  while (
    curr.isBefore(opts.minDate) ||
    opts.prohibited.some((h) =>
      dateInDaterange({ date: curr }, { from: h.firstday, until: h.lastday }),
    )
  ) {
    curr = curr.plusDays(7);
  }
  return curr;
}
